import { webSocketUrl } from '@/environment';
import { logger } from '@/services/logger';
import { useEffect, useRef } from 'react';
import { useAuth } from '../auth';

const MaxRetries = 10;

export const useWebSocket = (onMessage: (event: MessageEvent) => void) => {
    const socketRef = useRef<WebSocket | null>(null);
    const { accessToken, isAuthenticated } = useAuth();
    const retries = useRef<number>(0);
    const retryTimeout = useRef<NodeJS.Timeout | null>(null);

    const connect = (accessToken: string) => {
        socketRef.current = new WebSocket(`${webSocketUrl}?authorization=Bearer ${accessToken}`);
        socketRef.current.onopen = () => {
            retries.current = 0;
        };

        socketRef.current.onmessage = (event: MessageEvent) => {
            onMessage(event);
        };

        socketRef.current.onclose = () => {
            socketRef.current = null;
            retries.current = 0;

            if (isAuthenticated && accessToken) {
                connect(accessToken);
            }
        };

        socketRef.current.onerror = (event: Event) => {
            retries.current = retries.current + 1;

            if (retries.current !== MaxRetries) {
                logger.debug(`WebSocket connection failed, will retry again in ${retries.current * 500} ms`, event);
                retryTimeout.current = setTimeout(() => {
                    if (isAuthenticated && !socketRef.current) {
                        connect(accessToken);
                    }
                }, retries.current * 500);
            } else {
                logger.warn(`WebSocket failed to connect after ${MaxRetries} retries`, event);
            }
        };
    };

    useEffect(() => {
        if (isAuthenticated && !socketRef.current && accessToken) {
            connect(accessToken);
        }
        return () => {
            if (retryTimeout.current) {
                clearTimeout(retryTimeout.current);
            }
        };
    }, [accessToken]);

    return socketRef;
};
